import type { Event, EventListResponse } from './types/event';
import type { TerritoryResolution } from './types/territory';
import type { DiscoveryDescriptor, TerritoryCapabilities } from './discovery';
import { addDays, romeDay } from './returnVisits';

/**
 * First-view data contract (T204-INT of docs/IMPLEMENTATION_PLAN_SEO_LCP.md).
 *
 * The server embeds, inside the SSR document, the data the app would otherwise
 * request one after the other right after its JavaScript runs (capabilities,
 * `/api/territory?path=`, the "Imperdibili" gallery list, `/api/events?id=`,
 * the Esplora feed). The client uses an embedded part ONLY when the request it
 * is about to make is character-for-character the one recorded next to it.
 *
 * That "character for character" rule is why the strings are built here, once,
 * by pure functions that both the server (api/ssr.ts) and the client import:
 * there is no second copy of the parameter order or of the Europe/Rome day.
 *
 * Imports nothing but sibling shared modules (the scraper loads this package
 * through tsx as CommonJS — see the note in index.ts).
 */

// ---------------------------------------------------------------------------
// Bootstrap block (the JSON the server writes into the document)
// ---------------------------------------------------------------------------

/** Version of the block's shape. A client that does not know it ignores the block. */
export const SSR_BOOTSTRAP_VERSION = 1;

/** `id` of the `<script type="application/json">` element carrying the block. */
export const SSR_BOOTSTRAP_ELEMENT_ID = 'ssr-bootstrap';

/** `/api/territory` answer in the navigation-v2 lane (resolution + normalized address). */
export type TerritoryBootstrapBody = TerritoryResolution & {
  normalizedPath: string;
  normalizedSearch: string;
};

export interface SsrBootstrap {
  v: typeof SSR_BOOTSTRAP_VERSION;
  /** `window.location.pathname` the document was rendered for (compare before use). */
  path: string;
  /** What `GET /api/territory?mode=capabilities` answered when the document was built. */
  caps: TerritoryCapabilities;
  /** Equal to `territoryRequestOf(descriptor)`; `body` equals the API answer to it. */
  territory?: { request: string; body: TerritoryBootstrapBody };
  /** `query` equals the string `buildGalleryQuery` yields; `events` = 15 diversified events. */
  gallery?: { query: string; events: Event[] };
  /** `id` equals the route id; `body` equals the `/api/events?id=` answer. */
  event?: { id: string; body: Event };
  /**
   * First page of the Esplora feed. `query` equals `buildEventsFeedQuery(...)`;
   * `day` is the Europe/Rome date the document was built on (the feed query
   * carries no date of its own, so a document older than the current Rome day
   * must not be used); `body` equals the `/api/events?<query>` answer.
   */
  feed?: { query: string; day: string; body: EventListResponse };
}

// ---------------------------------------------------------------------------
// Territory request
// ---------------------------------------------------------------------------

/** Query keys of the canonical search that change what `/api/territory` returns. */
const TERRITORY_REQUEST_CONTENT_KEYS: ReadonlySet<string> = new Set([
  'categorie',
  'quando',
  'startDate',
  'endDate',
  'radiusKm',
  'search',
]);

/**
 * The query string the client sends to `/api/territory` for a descriptor
 * (without the leading `?`), or `null` for a route that is not territorial.
 * Moved verbatim from `DiscoveryContext.tsx` (`territoryRequest`). Map,
 * tracking and unknown parameters stay out on purpose.
 */
export function territoryRequestOf(descriptor: DiscoveryDescriptor): string | null {
  const route = descriptor.route;
  if (!('segments' in route)) return null;
  const params = new URLSearchParams({ path: descriptor.canonicalPath.slice('/eventi/'.length) });
  for (const [key, value] of new URLSearchParams(descriptor.canonicalSearch)) {
    if (TERRITORY_REQUEST_CONTENT_KEYS.has(key)) params.append(key, value);
  }
  return params.toString();
}

// ---------------------------------------------------------------------------
// Spatial scope shared by the gallery and the feed
// ---------------------------------------------------------------------------

export interface LatLng {
  lat: number;
  lng: number;
}

/** Radius a comune preset falls back to when a resolution carries no location radius. */
export const COMUNE_PRESET_RADIUS_KM = 25;

/**
 * The radius the territorial view is using: the URL's `radiusKm`, else the
 * registry default of the search area, else the preset of the resolved location.
 * Moved from `DiscoveryContext.tsx` (`activeLocation`).
 */
export function activeRadiusKm(
  resolution: TerritoryResolution,
  urlRadiusKm: number | undefined
): number {
  const presetRadius =
    resolution.location.type !== 'none' ? resolution.location.radiusKm : COMUNE_PRESET_RADIUS_KM;
  return urlRadiusKm ?? resolution.searchArea?.defaultRadiusKm ?? presetRadius;
}

/** Centers of the resolved location, as the feed queries them (`lat`/`lng` only). */
export function locationCentersOf(resolution: TerritoryResolution): LatLng[] | undefined {
  const location = resolution.location;
  if (location.type === 'cities') {
    return location.cities.length > 0
      ? location.cities.map((city) => ({ lat: city.lat, lng: city.lng }))
      : undefined;
  }
  return undefined;
}

/**
 * Centers of the "Imperdibili" gallery in v2: ALL search-area centers, else the
 * location's cities. Same rule as `useGalleryScope`.
 */
export function galleryCentersOf(resolution: TerritoryResolution): LatLng[] | undefined {
  const searchArea = resolution.searchArea?.centers;
  if (searchArea && searchArea.length > 0) return searchArea;
  return locationCentersOf(resolution);
}

// ---------------------------------------------------------------------------
// "Imperdibili" gallery
// ---------------------------------------------------------------------------

export const GALLERY_DAYS_AHEAD = 30;
export const GALLERY_LIMIT = 15;
export const GALLERY_PAGE_SIZE = 100;

/**
 * Effective window of the gallery: its own `[day, day + daysAhead]`, narrowed by
 * explicit URL dates. `null` when the two do not overlap (the gallery is then
 * honestly empty and no request is made).
 */
export function galleryWindow(input: {
  day: string;
  daysAhead: number;
  startDate?: string;
  endDate?: string;
}): { from: string; to: string } | null {
  const { day, daysAhead, startDate, endDate } = input;
  const windowEnd = addDays(day, daysAhead);
  const from = startDate && startDate > day ? startDate : day;
  const to = endDate && endDate < windowEnd ? endDate : windowEnd;
  return from > to ? null : { from, to };
}

/**
 * Query string (no leading `?`) of the gallery request. Parameter order and
 * values are those of `useTopRankedEvents` before T204. `day` (Europe/Rome) is
 * the cache-buster that flips the edge cache at Rome midnight, and it is also
 * what makes a stale document's embedded gallery stop matching.
 */
export function buildGalleryQuery(input: {
  day: string;
  from: string;
  to: string;
  centers?: LatLng[];
  radiusKm: number;
  category?: string;
  fallbackCity?: string;
}): string {
  const params = new URLSearchParams();
  params.set('orderBy', 'popularity');
  params.set('startDate', input.from);
  params.set('endDate', input.to);
  params.set('pageSize', String(GALLERY_PAGE_SIZE));
  params.set('day', input.day);
  if (input.category) params.set('category', input.category);
  if (input.centers) {
    params.set('centers', JSON.stringify(input.centers));
    params.set('radiusKm', String(input.radiusKm));
  } else if (input.fallbackCity) {
    params.set('city', input.fallbackCity);
  }
  return params.toString();
}

/**
 * The gallery query of a visitor without saved preferences on a territorial
 * page (what the server embeds), or `null` when the client would not make one
 * (no centers, disjoint dates).
 */
export function galleryQueryForTerritory(input: {
  now: Date;
  resolution: TerritoryResolution;
  descriptor: DiscoveryDescriptor;
}): string | null {
  const { now, resolution, descriptor } = input;
  if (resolution.location.type !== 'cities') return null;
  const centers = galleryCentersOf(resolution);
  if (!centers) return null;
  const day = romeDay(now);
  const window = galleryWindow({
    day,
    daysAhead: GALLERY_DAYS_AHEAD,
    startDate: descriptor.filters.startDate,
    endDate: descriptor.filters.endDate,
  });
  if (!window) return null;
  const category =
    descriptor.categoryIds.length > 0 ? [...descriptor.categoryIds].sort().join(',') : undefined;
  return buildGalleryQuery({
    day,
    from: window.from,
    to: window.to,
    centers,
    radiusKm: activeRadiusKm(resolution, descriptor.filters.radiusKm),
    category,
  });
}

/**
 * Round-robin diversification by category.
 *
 * Input: array of items already sorted by ranking (best first), each with a `category` field.
 * Output: subset of `items` (max length = `limit`) where:
 *   - Categories are visited in the order they first appear in the input
 *   - Round 1: take items[0] of each category
 *   - Round 2: take items[1] of each category (if exists)
 *   - ...continues until output has `limit` items OR all categories exhausted
 *
 * Properties guaranteed:
 *   - First N positions (N = distinct categories) contain at most 1 item per category
 *   - No two consecutive items share the same category WHILE round-robin is active
 *     (i.e., as long as more than one group still has remaining items)
 *   - When smaller groups exhaust, remaining items from larger groups are appended
 *     at the tail; adjacency is no longer guaranteed there (acceptable trade-off:
 *     better than discarding the extra ranked items)
 *
 * Pure function — deterministic given identical input. Moved here from the web
 * app (T204-INT) so the server builds the embedded gallery with the same code.
 */
export function diversifyByCategory<T extends { category: string }>(
  items: T[],
  limit: number
): T[] {
  if (limit <= 0 || items.length === 0) return [];

  // Group preserving original order; track first-appearance order of categories.
  const groups = new Map<string, T[]>();
  const categoryOrder: string[] = [];
  for (const item of items) {
    if (!groups.has(item.category)) {
      groups.set(item.category, []);
      categoryOrder.push(item.category);
    }
    groups.get(item.category)!.push(item);
  }

  // Round-robin: take 1 from each category in order, repeat until limit or exhaustion.
  const out: T[] = [];
  let round = 0;
  while (out.length < limit) {
    let picked = false;
    for (const cat of categoryOrder) {
      const group = groups.get(cat)!;
      if (round < group.length) {
        out.push(group[round]!);
        picked = true;
        if (out.length >= limit) break;
      }
    }
    if (!picked) break; // All groups exhausted
    round++;
  }

  return out;
}

// ---------------------------------------------------------------------------
// Esplora feed (`EventsContext`)
// ---------------------------------------------------------------------------

export const FEED_PAGE_SIZE = 30;
export const FEED_FIRST_PAGE = 1;

/**
 * Query string (no leading `?`) of an `EventsContext` list request. Parameter
 * order is the one `EventsContext` used before T204: centers (+ radius) or
 * city/bbox, category, dates, search, page, pageSize.
 */
export function buildEventsFeedQuery(input: {
  centers?: LatLng[];
  radiusKm?: number;
  city?: string;
  bbox?: { minLat: number; maxLat: number; minLng: number; maxLng: number };
  categories: string[];
  startDate?: string;
  endDate?: string;
  search?: string;
  page: number;
  pageSize: number;
}): string {
  const params = new URLSearchParams();
  if (input.centers && input.centers.length > 0) {
    params.append('centers', JSON.stringify(input.centers));
    if (input.radiusKm) params.append('radiusKm', String(input.radiusKm));
  } else {
    if (input.city) params.append('city', input.city);
    if (input.bbox) {
      params.append('minLat', String(input.bbox.minLat));
      params.append('maxLat', String(input.bbox.maxLat));
      params.append('minLng', String(input.bbox.minLng));
      params.append('maxLng', String(input.bbox.maxLng));
    }
  }
  if (input.categories.length > 0) params.append('category', input.categories.join(','));
  if (input.startDate) params.append('startDate', input.startDate);
  if (input.endDate) params.append('endDate', input.endDate);
  if (input.search) params.append('search', input.search);
  params.append('page', String(input.page));
  params.append('pageSize', String(input.pageSize));
  return params.toString();
}

/**
 * Date bounds of a v2 feed: the URL dates intersected with the resolved period.
 * The two bounds stay independent (an end-only filter does not invent a start).
 * Moved from `EventsContext.tsx` (`v2StartDate` / `v2EndDate`).
 */
export function feedDateBounds(
  descriptor: DiscoveryDescriptor,
  period: { from: string; to: string } | null | undefined
): { startDate?: string; endDate?: string } {
  let startDate = descriptor.filters.startDate;
  let endDate = descriptor.filters.endDate;
  if (period) {
    if (!startDate || startDate < period.from) startDate = period.from;
    if (!endDate || endDate > period.to) endDate = period.to;
  }
  return { startDate, endDate };
}

/**
 * The first-page feed query the client makes on a territorial v2 page, or
 * `null` when it would make none that the server can describe (no centers).
 */
export function feedQueryForTerritory(input: {
  resolution: TerritoryResolution;
  descriptor: DiscoveryDescriptor;
}): string | null {
  const { resolution, descriptor } = input;
  const centers = locationCentersOf(resolution);
  if (!centers) return null;
  const { startDate, endDate } = feedDateBounds(descriptor, resolution.period);
  return buildEventsFeedQuery({
    centers,
    radiusKm: activeRadiusKm(resolution, descriptor.filters.radiusKm),
    categories: descriptor.categoryIds,
    startDate,
    endDate,
    search: descriptor.filters.search,
    page: FEED_FIRST_PAGE,
    pageSize: FEED_PAGE_SIZE,
  });
}
